import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  formatCurrency,
  formatPercent,
  // getPerformanceClass,
} from "@/utils/formatters";
import ThemeToggle from "../ThemeToggle";
import { useAuth } from "../../contexts/AuthContext";

const OldDashboard = ({
  accounts,
  loading,
  onSelectAccount,
  onCreateAccount,
  onDeleteAccount,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [showAddForm, setShowAddForm] = useState(false);

  const handleAccountClick = ({ account }) => {
    onSelectAccount(account);
    navigate(`/old/account/${account.id}`);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading accounts...
      </div>
    );
  }

  return (
    <div
      className="dashboard"
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div className="dashboard-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="dashboard-title" data-testid="dashboard-title">
            WealthPort
          </h1>
          <p className="dashboard-subtitle" data-testid="dashboard-subtitle">
            Professional swing trading account tracker
            {user && (
              <span style={{ marginLeft: "0.5rem", opacity: 0.7 }}>
                • {user.email}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <ThemeToggle />
          <button
            className="btn btn-secondary"
            onClick={() => navigate("/roadmap")}
          >
            📋 Roadmap
          </button>
          <button
            className="btn btn-secondary"
            onClick={onRefresh}
            data-testid="refresh-button"
          >
            🔄 Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddForm(true)}
            data-testid="add-account-button"
          >
            ➕ Add Account
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              logout();
              navigate("/landing");
            }}
          >
            🚪 Logout
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "0 2rem 2rem 2rem" }}>
        {showAddForm && (
          <AddAccountForm
            onCreateAccount={onCreateAccount}
            onClose={() => setShowAddForm(false)}
          />
        )}

        {accounts.length === 0 && (
          <div className="empty-state" data-testid="empty-state">
            <h3>No accounts yet</h3>
            <p>Create your first account to start tracking your swing trades</p>
          </div>
        )}

        {accounts.length > 0 && (
          <div className="account-grid">
            {accounts.map((account) => (
              <AccountCard
                key={account.id}
                account={account}
                onAccountClick={handleAccountClick}
                onDeleteAccount={onDeleteAccount}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AccountCard = ({ account, onAccountClick, onDeleteAccount }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getPerformanceClass = (value) => {
    if (value > 0) return "performance-positive";
    if (value < 0) return "performance-negative";
    return "performance-neutral";
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (showDeleteConfirm) {
      try {
        await onDeleteAccount(account.id);
      } catch (error) {
        alert("Error deleting account");
      }
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  return (
    <div
      className="account-card"
      onClick={() => onAccountClick(account)}
      data-testid={`account-card-${account.id}`}
    >
      <div className="account-header">
        <div>
          <h3 className="account-name" data-testid="account-name">
            {account.name}
          </h3>
          <span className="account-type" data-testid="account-type">
            {account.account_type}
          </span>
        </div>
        <button
          className={`btn btn-danger ${showDeleteConfirm ? "confirm" : ""}`}
          onClick={handleDelete}
          data-testid="delete-account-button"
          style={{
            padding: "0.5rem",
            fontSize: "0.75rem",
            minWidth: showDeleteConfirm ? "auto" : "2rem",
          }}
        >
          {showDeleteConfirm ? "✓ Confirm" : "🗑"}
        </button>
      </div>

      <div className="account-stats">
        <div className="stat-item">
          <div className="stat-label">Account Balance</div>
          <div className="stat-value" data-testid="account-value">
            {formatCurrency(account.total_account_value || account.total_value)}
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Today's P&L</div>
          <div
            className={`stat-value ${getPerformanceClass(
              account.todays_gain_loss || account.total_gain_loss
            )}`}
            data-testid="todays-change"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            <span>
              {formatCurrency(
                account.todays_gain_loss || account.total_gain_loss || 0
              )}
            </span>
            <span className="text-muted" style={{ fontSize: "0.875rem" }}>
              {formatPercent(account.todays_gain_loss_percent || 0)}
            </span>
          </div>
        </div>
      </div>

      <div
        className="text-muted"
        style={{
          fontSize: "0.75rem",
          marginBottom: "0.5rem",
        }}
      >
        <div>
          <span>Stocks + Cash + Pending:</span>
          <br />
          <strong>
            {formatCurrency(account.stock_value || 0)} +{" "}
            {formatCurrency(account.cash_balance || 0)} +{" "}
            {formatCurrency(account.pending_value || 0)}
          </strong>
        </div>
      </div>

      <div
        className={`performance-indicator ${getPerformanceClass(
          account.total_gain_loss_percent
        )}`}
      >
        <span data-testid="account-performance">
          {formatPercent(account.total_gain_loss_percent)}
        </span>
      </div>
    </div>
  );
};

const AddAccountForm = ({ onCreateAccount, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    account_type: "",
  });
  const [loading, setLoading] = useState(false);

  // Handle escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.account_type) {
      alert("Please fill in all fields");
      return;
    }

    try {
      setLoading(true);
      await onCreateAccount(formData);
      onClose();
    } catch (error) {
      alert("Error creating account");
    } finally {
      setLoading(false);
    }
  };

  const accountTypes = [
    "Fidelity Taxable",
    "Fidelity Roth IRA",
    "Fidelity Traditional IRA",
    "Fidelity SEP IRA",
    "Fidelity HSA",
    "TD Ameritrade",
    "Schwab",
    "E*TRADE",
    "Other",
  ];

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="form-container" data-testid="add-account-form">
        <h2 style={{ marginBottom: "1.5rem", textAlign: "center" }}>
          Add New Account
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Account Name</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., Main Trading Account"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              data-testid="account-name-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Account Type</label>
            <select
              className="form-select"
              value={formData.account_type}
              onChange={(e) =>
                setFormData({ ...formData, account_type: e.target.value })
              }
              data-testid="account-type-select"
            >
              <option value="">Select account type</option>
              {accountTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
              data-testid="cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              data-testid="create-account-button"
            >
              {loading ? "Creating..." : "Create Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OldDashboard;
